import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import adminSettingsService from '../../../services/adminSettingsService';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ mt: 2 }}>{children}</Box>;
}

export default function WhatsAppSettings() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [companySettings, setCompanySettings] = useState(null);
  const [closureReasons, setClosureReasons] = useState([]);
  const [pendingReasons, setPendingReasons] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [dialog, setDialog] = useState({ open: false, kind: null, mode: 'create', row: null });

  const companyDraft = useMemo(() => {
    const s = companySettings || {};
    const auto = s.autoReplies || {};
    return {
      timezone: s.timezone || 'America/Sao_Paulo',
      retentionDays: s.retentionDays ?? 90,
      businessHours: s.businessHours || null,
      holidays: s.holidays || null,
      campaignSettings: s.campaignSettings || null,
      auditSettings: s.auditSettings || null,
      greetingEnabled: Boolean(auto.greetingEnabled),
      greetingMessage: auto.greetingMessage || '',
      absenceEnabled: Boolean(auto.absenceEnabled),
      absenceMessage: auto.absenceMessage || ''
    };
  }, [companySettings]);
  const [companyForm, setCompanyForm] = useState(companyDraft);

  useEffect(() => { setCompanyForm(companyDraft); }, [companyDraft]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cs, cr, pr, qr, tm] = await Promise.all([
        adminSettingsService.getCompanySettings(),
        adminSettingsService.listClosureReasons(),
        adminSettingsService.listPendingReasons(),
        adminSettingsService.listQuickReplies(),
        adminSettingsService.listTemplates()
      ]);
      setCompanySettings(cs?.data?.settings || cs?.data?.data?.settings || cs?.settings || null);
      setClosureReasons(cr?.data?.reasons || []);
      setPendingReasons(pr?.data?.reasons || []);
      setQuickReplies(qr?.data?.quickReplies || []);
      setTemplates(tm?.data?.templates || []);
    } catch {
      setCompanySettings(null);
      setClosureReasons([]);
      setPendingReasons([]);
      setQuickReplies([]);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll().catch(() => {}); }, []);

  const openDialog = (kind, mode, row = null) => setDialog({ open: true, kind, mode, row });
  const closeDialog = () => setDialog({ open: false, kind: null, mode: 'create', row: null });

  const saveCompany = async () => {
    setLoading(true);
    try {
      await adminSettingsService.updateCompanySettings({
        timezone: companyForm.timezone,
        retentionDays: Number(companyForm.retentionDays),
        businessHours: companyForm.businessHours,
        holidays: companyForm.holidays,
        campaignSettings: companyForm.campaignSettings,
        auditSettings: companyForm.auditSettings,
        autoReplies: {
          greetingEnabled: Boolean(companyForm.greetingEnabled),
          greetingMessage: companyForm.greetingMessage || null,
          absenceEnabled: Boolean(companyForm.absenceEnabled),
          absenceMessage: companyForm.absenceMessage || null
        }
      });
      await loadAll();
    } finally {
      setLoading(false);
    }
  };

  const renderReasons = (rows, kind) => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {kind === 'closure' ? 'Motivos de Encerramento' : 'Motivos de Pendência'}
        </Typography>
        <Button variant="outlined" onClick={() => openDialog(kind, 'create')}>Novo</Button>
      </Box>
      {rows.length === 0 ? (
        <EmptyState title="Nenhum motivo cadastrado" />
      ) : (
        <DataTable
          columns={[
            { key: 'name', label: 'Nome' },
            { key: 'description', label: 'Descrição' },
            { key: 'active', label: 'Ativo', align: 'right', render: (v) => (v ? 'Sim' : 'Não') },
            { key: 'actions', label: '', align: 'right', render: (_, row) => (
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="outlined" size="small" onClick={() => openDialog(kind, 'edit', row)}>Editar</Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={async () => {
                    if (!window.confirm('Remover este item?')) return;
                    if (kind === 'closure') await adminSettingsService.deleteClosureReason(row.id);
                    else await adminSettingsService.deletePendingReason(row.id);
                    await loadAll();
                  }}
                >
                  Remover
                </Button>
              </Box>
            ) }
          ]}
          rows={rows}
          getKey={(r) => r.id}
        />
      )}
    </Box>
  );

  const renderQuickReplies = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Respostas rápidas</Typography>
        <Button variant="outlined" onClick={() => openDialog('quickReply', 'create')}>Novo</Button>
      </Box>
      {quickReplies.length === 0 ? (
        <EmptyState title="Nenhuma resposta rápida cadastrada" />
      ) : (
        <DataTable
          columns={[
            { key: 'shortcut', label: 'Atalho' },
            { key: 'category', label: 'Categoria' },
            { key: 'message', label: 'Mensagem' },
            { key: 'active', label: 'Ativo', align: 'right', render: (v) => (v ? 'Sim' : 'Não') },
            { key: 'actions', label: '', align: 'right', render: (_, row) => (
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="outlined" size="small" onClick={() => openDialog('quickReply', 'edit', row)}>Editar</Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={async () => {
                    if (!window.confirm('Remover este item?')) return;
                    await adminSettingsService.deleteQuickReply(row.id);
                    await loadAll();
                  }}
                >
                  Remover
                </Button>
              </Box>
            ) }
          ]}
          rows={quickReplies}
          getKey={(r) => r.id}
        />
      )}
    </Box>
  );

  const renderTemplates = () => (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Templates</Typography>
        <Button variant="outlined" onClick={() => openDialog('template', 'create')}>Novo</Button>
      </Box>
      {templates.length === 0 ? (
        <EmptyState title="Nenhum template cadastrado" />
      ) : (
        <DataTable
          columns={[
            { key: 'name', label: 'Nome' },
            { key: 'category', label: 'Categoria' },
            { key: 'language', label: 'Idioma' },
            { key: 'status', label: 'Status' },
            { key: 'actions', label: '', align: 'right', render: (_, row) => (
              <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button variant="outlined" size="small" onClick={() => openDialog('template', 'edit', row)}>Editar</Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={async () => {
                    if (!window.confirm('Remover este item?')) return;
                    await adminSettingsService.deleteTemplate(row.id);
                    await loadAll();
                  }}
                >
                  Remover
                </Button>
              </Box>
            ) }
          ]}
          rows={templates}
          getKey={(r) => r.id}
        />
      )}
    </Box>
  );

  const dialogContent = (() => {
    if (!dialog.open) return null;
    if (dialog.kind === 'closure' || dialog.kind === 'pending') {
      const r = dialog.row || {};
      return (
        <ReasonEditor
          mode={dialog.mode}
          initial={{ name: r.name || '', description: r.description || '', active: r.active !== false }}
          onSave={async (payload) => {
            if (dialog.kind === 'closure') {
              if (dialog.mode === 'create') await adminSettingsService.createClosureReason(payload);
              else await adminSettingsService.updateClosureReason(r.id, payload);
            } else {
              if (dialog.mode === 'create') await adminSettingsService.createPendingReason(payload);
              else await adminSettingsService.updatePendingReason(r.id, payload);
            }
            closeDialog();
            await loadAll();
          }}
        />
      );
    }
    if (dialog.kind === 'quickReply') {
      const r = dialog.row || {};
      return (
        <QuickReplyEditor
          mode={dialog.mode}
          initial={{ shortcut: r.shortcut || '', message: r.message || '', category: r.category || 'general', active: r.active !== false }}
          onSave={async (payload) => {
            if (dialog.mode === 'create') await adminSettingsService.createQuickReply(payload);
            else await adminSettingsService.updateQuickReply(r.id, payload);
            closeDialog();
            await loadAll();
          }}
        />
      );
    }
    if (dialog.kind === 'template') {
      const r = dialog.row || {};
      return (
        <TemplateEditor
          mode={dialog.mode}
          initial={{ name: r.name || '', category: r.category || 'general', language: r.language || 'pt_BR', status: r.status || 'draft', components: r.components || {} , providerId: r.providerId || '' }}
          onSave={async (payload) => {
            if (dialog.mode === 'create') await adminSettingsService.createTemplate(payload);
            else await adminSettingsService.updateTemplate(r.id, payload);
            closeDialog();
            await loadAll();
          }}
        />
      );
    }
    return null;
  })();

  return (
    <Box p={3}>
      <Typography variant="h5" sx={{ mb: 2 }}>Configurações Administrativas</Typography>
      <Paper sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
          <Tab label="Empresa" />
          <Tab label="Motivos" />
          <Tab label="Respostas rápidas" />
          <Tab label="Templates" />
          <Tab label="Admin" />
        </Tabs>
        <Divider sx={{ my: 2 }} />

        {loading && <LoadingState />}

        {!loading && (
          <>
            <TabPanel value={tab} index={0}>
              {!companySettings ? (
                <EmptyState title="Sem configurações" description="Sem permissão ou erro ao carregar." />
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Timezone"
                      value={companyForm.timezone}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, timezone: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Retenção de logs (dias)"
                      type="number"
                      value={companyForm.retentionDays}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, retentionDays: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label='Feriados (JSON: ["YYYY-MM-DD"])'
                      value={JSON.stringify(companyForm.holidays || [], null, 0)}
                      onChange={(e) => {
                        try { setCompanyForm(prev => ({ ...prev, holidays: JSON.parse(e.target.value) })); } catch {}
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} />
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Horário comercial (JSON)"
                      value={JSON.stringify(companyForm.businessHours || {}, null, 2)}
                      onChange={(e) => {
                        try { setCompanyForm(prev => ({ ...prev, businessHours: JSON.parse(e.target.value) })); } catch {}
                      }}
                      multiline
                      minRows={6}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Parâmetros de campanha (JSON)"
                      value={JSON.stringify(companyForm.campaignSettings || {}, null, 2)}
                      onChange={(e) => {
                        try { setCompanyForm(prev => ({ ...prev, campaignSettings: JSON.parse(e.target.value) })); } catch {}
                      }}
                      multiline
                      minRows={6}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Parâmetros de auditoria (JSON)"
                      value={JSON.stringify(companyForm.auditSettings || {}, null, 2)}
                      onChange={(e) => {
                        try { setCompanyForm(prev => ({ ...prev, auditSettings: JSON.parse(e.target.value) })); } catch {}
                      }}
                      multiline
                      minRows={4}
                    />
                  </Grid>
                  <Grid item xs={12} />
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={<Switch checked={companyForm.greetingEnabled} onChange={(e) => setCompanyForm(prev => ({ ...prev, greetingEnabled: e.target.checked }))} />}
                      label="Saudação automática (dentro do horário)"
                    />
                    <TextField
                      fullWidth
                      label="Mensagem de saudação"
                      value={companyForm.greetingMessage}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, greetingMessage: e.target.value }))}
                      multiline
                      minRows={3}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={<Switch checked={companyForm.absenceEnabled} onChange={(e) => setCompanyForm(prev => ({ ...prev, absenceEnabled: e.target.checked }))} />}
                      label="Mensagem de ausência (fora do horário)"
                    />
                    <TextField
                      fullWidth
                      label="Mensagem de ausência"
                      value={companyForm.absenceMessage}
                      onChange={(e) => setCompanyForm(prev => ({ ...prev, absenceMessage: e.target.value }))}
                      multiline
                      minRows={3}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box display="flex" gap={1}>
                      <Button onClick={saveCompany}>Salvar</Button>
                      <Button variant="outlined" onClick={loadAll}>Recarregar</Button>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  {renderReasons(closureReasons, 'closure')}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderReasons(pendingReasons, 'pending')}
                </Grid>
              </Grid>
            </TabPanel>

            <TabPanel value={tab} index={2}>
              {renderQuickReplies()}
            </TabPanel>

            <TabPanel value={tab} index={3}>
              {renderTemplates()}
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Atalhos administrativos</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/whatsapp-connections')}>Conexões WhatsApp</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/queues')}>Filas</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/operations')}>Operação/Distribuição</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/sla')}>SLA</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/tags')}>Tags</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/users')}>Usuários e perfis</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/reports')}>Relatórios</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/pause-types')}>Tipos de pausa</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/external-api-integrations')}>Integrações API Externa</Button>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button fullWidth variant="outlined" onClick={() => navigate('/external-api-sends')}>Envios API Externa</Button>
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
      </Paper>

      <Dialog open={dialog.open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialog.kind === 'closure' ? (dialog.mode === 'create' ? 'Novo motivo de encerramento' : 'Editar motivo de encerramento') : null}
          {dialog.kind === 'pending' ? (dialog.mode === 'create' ? 'Novo motivo de pendência' : 'Editar motivo de pendência') : null}
          {dialog.kind === 'quickReply' ? (dialog.mode === 'create' ? 'Nova resposta rápida' : 'Editar resposta rápida') : null}
          {dialog.kind === 'template' ? (dialog.mode === 'create' ? 'Novo template' : 'Editar template') : null}
        </DialogTitle>
        <DialogContent>{dialogContent}</DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeDialog}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ReasonEditor({ initial, onSave }) {
  const [form, setForm] = useState(initial);
  return (
    <Box sx={{ mt: 1 }}>
      <TextField fullWidth label="Nome" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} sx={{ mb: 2 }} />
      <TextField fullWidth label="Descrição" value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} sx={{ mb: 2 }} />
      <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))} />} label="Ativo" />
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button onClick={() => onSave({ name: form.name, description: form.description || null, active: form.active })}>Salvar</Button>
      </Box>
    </Box>
  );
}

function QuickReplyEditor({ initial, onSave }) {
  const [form, setForm] = useState(initial);
  return (
    <Box sx={{ mt: 1 }}>
      <TextField fullWidth label="Atalho" value={form.shortcut} onChange={(e) => setForm(prev => ({ ...prev, shortcut: e.target.value }))} sx={{ mb: 2 }} />
      <TextField fullWidth label="Categoria" value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))} sx={{ mb: 2 }} />
      <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))} />} label="Ativo" />
      <TextField fullWidth label="Mensagem" value={form.message} onChange={(e) => setForm(prev => ({ ...prev, message: e.target.value }))} multiline minRows={4} />
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button onClick={() => onSave({ shortcut: form.shortcut, category: form.category, active: form.active, message: form.message })}>Salvar</Button>
      </Box>
    </Box>
  );
}

function TemplateEditor({ initial, onSave }) {
  const [form, setForm] = useState(initial);
  return (
    <Box sx={{ mt: 1 }}>
      <TextField fullWidth label="Nome" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} sx={{ mb: 2 }} />
      <TextField fullWidth label="Categoria" value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))} sx={{ mb: 2 }} />
      <TextField fullWidth label="Idioma" value={form.language} onChange={(e) => setForm(prev => ({ ...prev, language: e.target.value }))} sx={{ mb: 2 }} />
      <TextField fullWidth label="Status" value={form.status} onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))} sx={{ mb: 2 }} />
      <TextField fullWidth label="Provider ID" value={form.providerId} onChange={(e) => setForm(prev => ({ ...prev, providerId: e.target.value }))} sx={{ mb: 2 }} />
      <TextField
        fullWidth
        label="Components (JSON)"
        value={JSON.stringify(form.components || {}, null, 2)}
        onChange={(e) => {
          try { setForm(prev => ({ ...prev, components: JSON.parse(e.target.value) })); } catch {}
        }}
        multiline
        minRows={6}
      />
      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Button onClick={() => onSave({ name: form.name, category: form.category, language: form.language, status: form.status, providerId: form.providerId || null, components: form.components || {} })}>Salvar</Button>
      </Box>
    </Box>
  );
}
